

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { CalendarIcon, MapPinIcon, Pencil, Trash2, Plus, Users, Truck as TruckIcon, Car, Loader2, Save, AlertTriangle } from 'lucide-react';
import { toast } from "sonner";
import { format, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import {
    createCraftsmanAssignment,
    deleteCraftsmanAssignment,
    createTruckAssignment,
    deleteTruckAssignment,
    getDailyActivitiesForProject,
    upsertDailyActivityNotes
} from '@/features/projects/api';
import { Badge } from '@/shared/ui/badge';
import { Checkbox } from '@/shared/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { Textarea } from '@/shared/ui/textarea';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import type { Craftsman, TruckDriver, Truck, CompanyVehicle, ProjectZone, CraftsmanAssignment, TruckAssignment } from '@/types';


/**
 * リソース編集ポップアップのプロパティ
 */
interface ResourceEditPopupProps {
    projectId: string;
    date: string;
    zoneId: string | null;
    craftsmen: Craftsman[];
    truckDrivers: TruckDriver[];
    trucks: Truck[];
    companyVehicles: CompanyVehicle[];
    zones?: ProjectZone[]; // 工区情報を追加
    isOpen: boolean;
    onClose: () => void;
    onSaveSuccess?: () => void;
    isReadOnly?: boolean;
}

/**
 * リソース編集ポップアップ
 * @param param0 リソース編集ポップアップのプロパティ
 * @returns リソース編集ポップアップ
 */
export const ResourceEditPopup: React.FC<ResourceEditPopupProps> = ({
    projectId,
    date,
    zoneId,
    craftsmen,
    truckDrivers,
    trucks,
    companyVehicles,
    zones = [],
    isOpen,
    onClose,
    onSaveSuccess,
    isReadOnly = false,
}) => {
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<string>("assigned"); // デフォルトタブ

    // 職人割り当てリスト
    const [craftsmanAssignments, setCraftsmanAssignments] = useState<CraftsmanAssignment[]>([]);
    // トラック割り当てリスト
    const [truckAssignments, setTruckAssignments] = useState<TruckAssignment[]>([]);
    // アクティビティデータ
    const [activityData, setActivityData] = useState<any>(null);

    // 全体メモ
    const [overallMemo, setOverallMemo] = useState<string>('');
    const [memoSaving, setMemoSaving] = useState(false);

    // トラック割り当て用の状態
    const [newTruckId, setNewTruckId] = useState<string>('');
    const [newDriverId, setNewDriverId] = useState<string>('none');

    // 職人割り当て用の状態
    const [selectedVehicleId, setSelectedVehicleId] = useState<string>('none');
    const [selectedCraftsmen, setSelectedCraftsmen] = useState<string[]>([]);

    // 工区名
    const [zoneName, setZoneName] = useState<string>('');

    // すべてクリア確認ダイアログ
    const [showClearAllDialog, setShowClearAllDialog] = useState(false);
    const [clearingAll, setClearingAll] = useState(false);

    // データの取得関数をuseCallbackで定義
    const fetchActivityData = useCallback(async () => {
        if (!date || !projectId) return;

        setLoading(true);

        try {
            // 日次活動データを取得
            const activityResponse = await getDailyActivitiesForProject(projectId, {
                startDate: date,
                endDate: date
            });

            if (activityResponse.error) {
                throw new Error(activityResponse.error.message || '日次活動データの取得に失敗しました');
            }


            // 対象の日付の特定の工区のアクティビティを検索
            const targetActivity = activityResponse.data?.find(activity => {
                // データベーススキーマに合わせてschedule_dateを使用、後方互換性のためactivity_dateもチェック
                const activityDate = activity.schedule_date;
                return activityDate && activityDate.startsWith(date) &&
                    (activity.zone_id === zoneId || (zoneId === null && activity.zone_id === null));
            });

            setActivityData(targetActivity || null);

            // 工区名を設定
            if (targetActivity) {
                // 明示的に渡されたzoneIdがある場合、関連する工区を探す
                if (zoneId) {
                    // この日付のアクティビティAPIレスポンスから該当zoneIdの工区を探す
                    const allZones = activityResponse.data || [];

                    const matchingZoneActivity = allZones.find(activity =>
                        activity.zone_id === zoneId
                    );

                    // まず、zones配列から工区名を取得を試行
                    const zoneFromProps = zones.find(z => z.id === zoneId);
                    if (zoneFromProps && (zoneFromProps.name || zoneFromProps.zone_name)) {
                        setZoneName(zoneFromProps.name || zoneFromProps.zone_name || '');
                    } else if (matchingZoneActivity && matchingZoneActivity.project_zone && matchingZoneActivity.project_zone.zone_name) {
                        setZoneName(matchingZoneActivity.project_zone.zone_name);
                    } else if (targetActivity.project_zone && targetActivity.project_zone.zone_name) {
                        // バックアップとしてtargetActivityのproject_zoneを使用
                        setZoneName(targetActivity.project_zone.zone_name);
                    } else {
                        // まだ見つからない場合はActivityオブジェクトに関連するzone情報を探す
                        // APIレスポンスの構造に基づいて適切なパスを試行
                        const zoneInfo = allZones.find(activity =>
                            activity.zone_id === zoneId && activity.project_zone
                        )?.project_zone;


                        if (zoneInfo && zoneInfo.zone_name) {
                            setZoneName(zoneInfo.zone_name);
                        } else {
                            setZoneName('不明な工区');
                        }
                    }
                }
                // targetActivityのproject_zoneオブジェクトがある場合
                else if (targetActivity.project_zone && targetActivity.project_zone.zone_name) {
                    setZoneName(targetActivity.project_zone.zone_name);
                }
                // zone_idはあるがproject_zoneがない場合
                else if (targetActivity.zone_id) {
                    setZoneName('不明な工区');
                }
                // 工区IDがない場合は「全体」とする
                else {
                    setZoneName('全体');
                }
            } else {
                // アクティビティデータがない場合
                if (zoneId) {
                    // この日付のアクティビティAPIレスポンスから該当zoneIdの工区を探す
                    const allZones = activityResponse.data || [];
                    const matchingZoneActivity = allZones.find(activity =>
                        activity.zone_id === zoneId
                    );

                    if (matchingZoneActivity && matchingZoneActivity.project_zone && matchingZoneActivity.project_zone.zone_name) {
                        setZoneName(matchingZoneActivity.project_zone.zone_name);
                    } else {
                        setZoneName('不明な工区');
                    }
                } else {
                    setZoneName('全体');
                }
            }

            // 既存の割り当てを設定
            if (targetActivity) {
                setCraftsmanAssignments(targetActivity.craftsman_assignments || []);
                setTruckAssignments(targetActivity.truck_assignments || []);
                setOverallMemo(targetActivity.notes || '');
            } else {
                setCraftsmanAssignments([]);
                setTruckAssignments([]);
                setOverallMemo('');
            }
        } catch (error) {
            console.error('Failed to fetch activity data:', error);
            toast.error("データ取得エラー", {
                description: error instanceof Error ? error.message : "アクティビティデータの取得中にエラーが発生しました",
            });
        } finally {
            setLoading(false);
        }
    }, [projectId, date, zoneId, zones]);

    // データの取得
    useEffect(() => {
        if (isOpen) {
            fetchActivityData();
        }
    }, [isOpen, fetchActivityData]);

    // 車両ごとの複数職人割り当て
    const handleAddVehicleWithCraftsmen = async () => {
        if (selectedCraftsmen.length === 0) return;
        setLoading(true);

        try {
            // 選択された各職人に対して割り当てを作成
            const promises = selectedCraftsmen.map(craftsmanId =>
                createCraftsmanAssignment({
                    projectId,
                    date,
                    zoneId,
                    craftsmanId,
                    vehicleId: selectedVehicleId && selectedVehicleId !== 'none' ? selectedVehicleId : null,
                    notes: ''
                })
            );

            const results = await Promise.all(promises);

            // エラーチェック
            const errors = results.filter(r => r.error).map(r => r.error?.message);
            if (errors.length > 0) {
                throw new Error(`職人割り当て中にエラーが発生しました: ${errors.join(', ')}`);
            }


            // 親コンポーネントの更新処理を先に実行
            if (onSaveSuccess) {
                await onSaveSuccess();
            }

            // 親コンポーネントの更新完了後にローカルデータを更新
            await fetchActivityData();

            // 入力内容をクリア
            setSelectedCraftsmen([]);
            setSelectedVehicleId('none');

            // 割り当て済みタブに切り替え
            setActiveTab("assigned");

            // 成功メッセージ
            toast.success("追加完了", {
                description: `${selectedCraftsmen.length}名の職人を割り当てました`,
            });

        } catch (error) {
            console.error('Failed to add craftsmen with vehicle:', error);
            toast.error("エラー", {
                description: error instanceof Error ? error.message : '職人の割り当てに失敗しました',
            });
        } finally {
            setLoading(false);
        }
    };

    // 職人の割り当て削除
    const handleDeleteCraftsmanAssignment = async (assignmentId: string) => {
        if (!confirm('この職人の割り当てを削除しますか？')) return;
        setLoading(true);

        try {
            const response = await deleteCraftsmanAssignment(assignmentId);

            if (response.error) {
                throw new Error(response.error.message);
            }

            // 親コンポーネントの更新処理を先に実行
            if (onSaveSuccess) {
                await onSaveSuccess();
            }

            // 親コンポーネントの更新完了後にローカルデータを更新
            await fetchActivityData();

            toast.success("削除完了", {
                description: "職人の割り当てを削除しました",
            });
        } catch (error) {
            console.error('Failed to delete craftsman assignment:', error);
            toast.error("エラー", {
                description: error instanceof Error ? error.message : '職人の割り当て削除に失敗しました',
            });
        } finally {
            setLoading(false);
        }
    };

    // トラックの割り当て追加
    const handleAddTruckAssignment = async () => {
        if (!newTruckId) return;
        setLoading(true);

        try {
            const response = await createTruckAssignment({
                projectId,
                date,
                zoneId,
                truckId: newTruckId,
                driverId: newDriverId && newDriverId !== "none" ? newDriverId : null,
                notes: ''
            });

            if (response.error) {
                throw new Error(response.error.message);
            }

            // 成功したらフォームをリセットしてデータを再取得
            setNewTruckId('');
            setNewDriverId('none');

            // 親コンポーネントの更新処理を先に実行
            if (onSaveSuccess) {
                await onSaveSuccess();
            }

            // 親コンポーネントの更新完了後にローカルデータを更新
            await fetchActivityData();

            // 割り当て済みタブに切り替え
            setActiveTab("assigned");

            toast.success("追加完了", {
                description: "トラックの割り当てを追加しました",
            });
        } catch (error) {
            console.error('Failed to add truck assignment:', error);
            toast.error("エラー", {
                description: error instanceof Error ? error.message : "トラックの割り当て追加に失敗しました",
            });
        } finally {
            setLoading(false);
        }
    };

    // トラックの割り当て削除
    const handleDeleteTruckAssignment = async (assignmentId: string) => {
        if (!confirm('このトラックの割り当てを削除しますか？')) return;
        setLoading(true);

        try {
            const response = await deleteTruckAssignment(assignmentId);

            if (response.error) {
                throw new Error(response.error.message);
            }

            // 親コンポーネントの更新処理を先に実行
            if (onSaveSuccess) {
                await onSaveSuccess();
            }

            // 親コンポーネントの更新完了後にローカルデータを更新
            await fetchActivityData();

            toast.success("削除完了", {
                description: "トラックの割り当てを削除しました",
            });
        } catch (error) {
            console.error('Failed to delete truck assignment:', error);
            toast.error("エラー", {
                description: error instanceof Error ? error.message : "トラックの割り当て削除に失敗しました",
            });
        } finally {
            setLoading(false);
        }
    };

    // チェックボックス選択状態の切り替え
    const toggleCraftsmanSelection = (craftsmanId: string) => {
        setSelectedCraftsmen(prevSelected => {
            if (prevSelected.includes(craftsmanId)) {
                return prevSelected.filter(id => id !== craftsmanId);
            } else {
                return [...prevSelected, craftsmanId];
            }
        });
    };

    // 全体メモの保存
    const handleSaveOverallMemo = async () => {
        setMemoSaving(true);

        try {
            const result = await upsertDailyActivityNotes({
                projectId,
                date,
                zoneId,
                notes: overallMemo
            });

            if (result.error) {
                throw new Error(result.error.message);
            }

            // 親コンポーネントの更新処理を先に実行
            if (onSaveSuccess) {
                await onSaveSuccess();
            }

            // 親コンポーネントの更新完了後にローカルデータを更新
            await fetchActivityData();

            toast.success("メモを保存しました");
        } catch (error) {
            console.error('Failed to save overall memo:', error);
            toast.error("メモの保存に失敗しました", {
                description: error instanceof Error ? error.message : "メモの保存中にエラーが発生しました",
            });
        } finally {
            setMemoSaving(false);
        }
    };

    // すべての割り当てをクリア
    const handleClearAllAssignments = async () => {
        setClearingAll(true);

        try {
            // 職人割り当てをすべて削除
            const craftsmanDeletions = craftsmanAssignments.map(assignment =>
                deleteCraftsmanAssignment(assignment.id)
            );

            // トラック割り当てをすべて削除
            const truckDeletions = truckAssignments.map(assignment =>
                deleteTruckAssignment(assignment.id)
            );

            // メモ削除（空文字で上書き）
            const memoDeletion = upsertDailyActivityNotes({
                projectId,
                date,
                zoneId,
                notes: ""
            });

            // 並列で削除処理を実行
            await Promise.all([...craftsmanDeletions, ...truckDeletions, memoDeletion]);

            // 親コンポーネントの更新処理を先に実行
            if (onSaveSuccess) {
                await onSaveSuccess();
            }

            // 親コンポーネントの更新完了後にローカルデータを更新
            await fetchActivityData();

            toast.success("すべての割り当てとメモをクリアしました");

        } catch (error) {
            console.error('Error clearing all assignments:', error);
            toast.error("クリアエラー", {
                description: error instanceof Error ? error.message : "割り当てまたはメモのクリア中にエラーが発生しました",
            });
        } finally {
            setClearingAll(false);
            setShowClearAllDialog(false);
        }
    };

    // 閉じる前のリセット処理
    const handleDialogClose = () => {
        setNewTruckId('');
        setNewDriverId('none');
        setSelectedVehicleId('none');
        setSelectedCraftsmen([]);
        setCraftsmanAssignments([]);
        setTruckAssignments([]);
        setOverallMemo('');
        setActiveTab("assigned"); // デフォルトタブにリセット
        setShowClearAllDialog(false); // 確認ダイアログを閉じる
        setClearingAll(false); // クリア状態をリセット
        onClose();
    };

    // すでに割り当て済みの職人IDを取得（重複選択防止用）
    const assignedCraftsmenIds = craftsmanAssignments.map(assignment => assignment.craftsman_id);

    // 割り当て済みリソースの表示（職人とトラック）
    const AssignedResourcesSection = () => (
        <div className="space-y-4">
            {/* クリアボタン - 割り当てがある場合のみ表示 */}
            {!isReadOnly && (craftsmanAssignments.length > 0 || truckAssignments.length > 0) && (
                <div className="flex justify-end">
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setShowClearAllDialog(true)}
                        disabled={loading || clearingAll}
                        className="flex items-center gap-2"
                    >
                        <AlertTriangle className="h-4 w-4" />
                        {clearingAll ? '削除中...' : 'すべてクリア'}
                    </Button>
                </div>
            )}
            <div className="space-y-2">
                <h4 className="text-sm font-medium flex items-center"><Users className="h-4 w-4 mr-1" /> 職人</h4>
                {craftsmanAssignments.length > 0 ? (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {craftsmanAssignments.map((assignment) => (
                            <div
                                key={assignment.id}
                                className="flex justify-between items-start p-2 border rounded-md"
                            >
                                <div className="space-y-1 w-full">
                                    <div className="font-medium flex items-center justify-between">
                                        <span>{assignment.craftsman_name ||
                                            (assignment.craftsman_id && craftsmen.find(c => c.id === assignment.craftsman_id)?.name) ||
                                            '不明な職人'}</span>
                                        {!isReadOnly && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleDeleteCraftsmanAssignment(assignment.id)}
                                                disabled={loading}
                                                className="h-7 w-7"
                                            >
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        )}
                                    </div>

                                    {assignment.vehicle_id && (
                                        <Badge variant="outline" className="mt-1 flex items-center gap-1 w-fit">
                                            <Car className="mr-1 h-3 w-3" />
                                            {assignment.company_vehicle_name ||
                                                (assignment.vehicle_id ? `社用車: ${assignment.vehicle_id.substring(0, 8)}...` : '社用車あり')}
                                        </Badge>
                                    )}

                                    {(assignment as any).remarks && (
                                        <div className="text-sm text-muted-foreground mt-1 border-t pt-1">
                                            <span className="font-medium text-xs">メモ: </span>
                                            {(assignment as any).remarks}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground">割り当てられた職人はいません</p>
                )}
            </div>

            <div className="space-y-2">
                <h4 className="text-sm font-medium flex items-center"><TruckIcon className="h-4 w-4 mr-1" /> トラック</h4>
                {truckAssignments.length > 0 ? (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {truckAssignments.map((assignment) => (
                            <div
                                key={assignment.id}
                                className="flex justify-between items-start p-2 border rounded-md"
                            >
                                <div className="space-y-1 w-full">
                                    <div className="font-medium flex items-center justify-between">
                                        <span>{(assignment.truck_id && trucks.find(t => t.id === assignment.truck_id)?.name) ||
                                            '不明なトラック'}</span>
                                        {!isReadOnly && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleDeleteTruckAssignment(assignment.id)}
                                                disabled={loading}
                                                className="h-7 w-7"
                                            >
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        )}
                                    </div>

                                    {/* 運転手情報の表示 - 常に何らかの情報を表示 */}
                                    <Badge variant="secondary" className="mt-1 flex items-center gap-1 w-fit">
                                        <Users className="mr-1 h-3 w-3" />
                                        {assignment.driver_id ?
                                            `運転手: ${(assignment.driver_id && truckDrivers.find(d => d.id === assignment.driver_id)?.name) ||
                                            (assignment.driver_id ? `不明 (ID: ${assignment.driver_id.substring(0, 8)}...)` : '不明な運転手')}` :
                                            '運転手なし'}
                                    </Badge>

                                    {(assignment as any).remarks && (
                                        <div className="text-sm text-muted-foreground mt-1 border-t pt-1">
                                            <span className="font-medium text-xs">メモ: </span>
                                            {(assignment as any).remarks}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground">割り当てられたトラックはありません</p>
                )}
            </div>
        </div>
    );

    // 新規リソース割り当てセクション（職人とトラック）
    const NewAssignmentSection = () => (
        <div className="space-y-6">
            {/* 職人割り当て */}
            <div className="space-y-3 p-3 border rounded-md bg-muted/50">
                <h4 className="text-sm font-medium flex items-center">
                    <Users className="h-4 w-4 mr-1" /> 職人割り当て
                </h4>

                <div className="space-y-3">
                    <div className="space-y-2">
                        <Label htmlFor="selected-vehicle">社用車を選択（任意）</Label>
                        <Select
                            value={selectedVehicleId}
                            onValueChange={setSelectedVehicleId}
                        >
                            <SelectTrigger id="selected-vehicle">
                                <SelectValue placeholder="社用車を選択" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">社用車なし</SelectItem>
                                {companyVehicles.map((vehicle) => (
                                    <SelectItem key={vehicle.id} value={vehicle.id}>
                                        {vehicle.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label>割り当てる職人を選択</Label>
                        <div className="border rounded-md p-3 max-h-[200px] overflow-y-auto">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {craftsmen
                                    .filter(craftsman => !assignedCraftsmenIds.includes(craftsman.id))
                                    .map(craftsman => (
                                        <div key={craftsman.id} className="flex items-center space-x-2">
                                            <Checkbox
                                                id={`craftsman-${craftsman.id}`}
                                                checked={selectedCraftsmen.includes(craftsman.id)}
                                                onCheckedChange={() => toggleCraftsmanSelection(craftsman.id)}
                                            />
                                            <Label htmlFor={`craftsman-${craftsman.id}`} className="cursor-pointer">
                                                {craftsman.name}
                                            </Label>
                                        </div>
                                    ))}
                                {craftsmen.filter(craftsman => !assignedCraftsmenIds.includes(craftsman.id)).length === 0 && (
                                    <p className="text-sm text-muted-foreground col-span-2">
                                        利用可能な職人がいません。すべての職人が割り当て済みです。
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>



                    <Button
                        onClick={handleAddVehicleWithCraftsmen}
                        disabled={selectedCraftsmen.length === 0 || loading}
                        className="w-full mt-2"
                    >
                        <Plus className="mr-1 h-4 w-4" />
                        {selectedCraftsmen.length}人の職人を{selectedVehicleId && selectedVehicleId !== 'none' ? "車両と共に" : ""}割り当て
                    </Button>
                </div>
            </div>

            {/* トラック割り当て */}
            <div className="space-y-3 p-3 border rounded-md bg-muted/50">
                <h4 className="text-sm font-medium flex items-center">
                    <TruckIcon className="h-4 w-4 mr-1" /> トラック割り当て
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <Label htmlFor="truck">トラック</Label>
                        <Select
                            value={newTruckId}
                            onValueChange={setNewTruckId}
                        >
                            <SelectTrigger id="truck">
                                <SelectValue placeholder="トラックを選択" />
                            </SelectTrigger>
                            <SelectContent>
                                {trucks.map((truck) => (
                                    <SelectItem key={truck.id} value={truck.id}>
                                        {truck.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="driver">運転手（任意）</Label>
                        <Select
                            value={newDriverId}
                            onValueChange={setNewDriverId}
                        >
                            <SelectTrigger id="driver">
                                <SelectValue placeholder="運転手を選択" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">運転手なし</SelectItem>
                                {truckDrivers.map((driver) => (
                                    <SelectItem key={driver.id} value={driver.id}>
                                        {driver.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>



                <Button
                    onClick={handleAddTruckAssignment}
                    disabled={!newTruckId || loading}
                    className="w-full mt-3"
                >
                    <Plus className="mr-1 h-4 w-4" />
                    トラックを追加
                </Button>
            </div>
        </div>
    );

    return (
        <Dialog open={isOpen} onOpenChange={handleDialogClose}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>リソース割り当て編集</DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground mt-1">
                        日付と工区ごとのリソース割り当てを編集できます
                    </DialogDescription>
                    <div className="text-sm text-muted-foreground mt-1 flex items-center space-x-2">
                        <CalendarIcon className="h-4 w-4" />
                        <span>{date ? format(parseISO(date), 'yyyy年MM月dd日 (E)', { locale: ja }) : ''}</span>
                        {zoneId && (
                            <>
                                <MapPinIcon className="h-4 w-4 ml-2" />
                                <span>
                                    工区: {zoneName || (zones.find(z => z.id === zoneId)?.name) || '不明な工区'}
                                </span>
                            </>
                        )}
                    </div>
                </DialogHeader>

                <div className="py-4">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                        <TabsList className={`grid w-full ${isReadOnly ? 'grid-cols-2' : 'grid-cols-3'}`}>
                            <TabsTrigger value="assigned" className="text-sm">
                                割り当て済み
                                {(craftsmanAssignments.length > 0 || truckAssignments.length > 0) && (
                                    <Badge variant="secondary" className="ml-2">
                                        {craftsmanAssignments.length + truckAssignments.length}
                                    </Badge>
                                )}
                            </TabsTrigger>
                            {!isReadOnly && (
                                <TabsTrigger value="new" className="text-sm">新規割り当て</TabsTrigger>
                            )}
                            <TabsTrigger value="memo" className="text-sm">
                                全体メモ
                                {overallMemo.trim() && (
                                    <Badge variant="secondary" className="ml-2">
                                        有
                                    </Badge>
                                )}
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="assigned" className="mt-4 space-y-4">
                            <AssignedResourcesSection />
                        </TabsContent>

                        {!isReadOnly && (
                            <TabsContent value="new" className="mt-4 space-y-4">
                                <NewAssignmentSection />
                            </TabsContent>
                        )}

                        <TabsContent value="memo" className="mt-4 space-y-4">
                            <div className="space-y-3 p-3 border rounded-md bg-muted/50">
                                <h4 className="text-sm font-medium flex items-center">
                                    <Pencil className="h-4 w-4 mr-1" /> 日次活動全体メモ
                                    {isReadOnly && (
                                        <Badge variant="secondary" className="ml-2 text-xs">閲覧のみ</Badge>
                                    )}
                                </h4>
                                <p className="text-xs text-muted-foreground">
                                    {isReadOnly
                                        ? "この日付・工区の全体的なメモを閲覧できます"
                                        : "この日付・工区の全体的なメモや注意事項を記入できます"
                                    }
                                </p>
                                <div className="space-y-2">
                                    <Textarea
                                        placeholder={isReadOnly ? "メモがありません" : "全体的なメモや注意事項を入力..."}
                                        value={overallMemo}
                                        onChange={(e) => setOverallMemo(e.target.value)}
                                        className="min-h-[120px] resize-none"
                                        disabled={memoSaving || isReadOnly}
                                        readOnly={isReadOnly}
                                    />
                                </div>
                                {!isReadOnly && (
                                    <Button
                                        onClick={handleSaveOverallMemo}
                                        disabled={memoSaving}
                                        className="w-full mt-3"
                                    >
                                        {memoSaving ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                保存中...
                                            </>
                                        ) : (
                                            <>
                                                <Save className="mr-1 h-4 w-4" />
                                                メモを保存
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleDialogClose}>
                        閉じる
                    </Button>
                </DialogFooter>
            </DialogContent>

            {/* すべてクリア確認ダイアログ */}
            <AlertDialog open={showClearAllDialog} onOpenChange={setShowClearAllDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-destructive" />
                            すべての割り当てをクリア
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            この日付・工区のすべての職人とトラックの割り当てを削除します。
                            <br />
                            <strong>この操作は元に戻せません。</strong>
                            <br />
                            削除対象:
                            <ul className="mt-2 list-disc list-inside text-sm">
                                {craftsmanAssignments.length > 0 && (
                                    <li>職人 {craftsmanAssignments.length}件</li>
                                )}
                                {truckAssignments.length > 0 && (
                                    <li>トラック {truckAssignments.length}件</li>
                                )}
                            </ul>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={clearingAll}>
                            キャンセル
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleClearAllAssignments}
                            disabled={clearingAll}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {clearingAll ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    削除中...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    すべて削除
                                </>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Dialog>
    );
}; 